import { useCallback, useState } from 'react'

import { clsx } from 'clsx'

import { ColorScheme, Palette, Theme } from '@tribeplatform/design-system/types'
import { getDomainInfo } from '@tribeplatform/react-components/common/utils'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { ColorPicker } from '@tribeplatform/react-ui-kit/ColorPicker'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { generateTheme } from '@tribeplatform/react-ui-kit/ThemeProvider'

import { SidebarBackButton } from '../../AdminMenu/SidebarBackButton.js'
import { SidebarContent } from '../../AdminMenu/SidebarContent.js'
import { useCustomizer, useCustomizerTheme } from '../hooks/index.js'
import { useCustomizerPreview } from '../hooks/use-customizer-preview.hook.js'
import { CustomizerActionType } from '../types.js'
import { AppearanceEditTheme } from './AppearanceEditTheme.js'
import { ColorPresets } from './components/ColorPresets.js'
import { StyleSelector } from './components/StyleSelector.js'
import { ThemeCopyPaste } from './components/ThemeCopyPaste.js'
import { ThemeMode } from './components/ThemeMode.js'

export const AppearanceColors = () => {
  const { $t } = useI18n()
  const [showPresets, setShowPresets] = useState(false)
  const [editThemeMode, setEditThemeMode] = useState<ColorScheme>()

  const { dispatch } = useCustomizer()
  const { setPreviewDarkModeDisabled } = useCustomizerPreview()

  const {
    appearance: { theme: currentTheme },
  } = useCustomizerTheme()

  const [isGeneratingPalette, setGeneratingPalette] = useState(false)

  const updateCustomTheme = useCallback(
    async (theme: Theme) => {
      dispatch({
        type: CustomizerActionType.NEW_UPDATE,
        payload: {
          theme: {
            typography: {
              colorTheme: theme.id,
              customTheme: theme,
            },
          },
        },
      })
    },
    [dispatch],
  )

  const {
    data: { networkPublicInfo },
  } = useAuthToken()
  const { subfolder } = getDomainInfo({
    domain: networkPublicInfo?.domain,
    domainSubfolder: networkPublicInfo?.domainSubfolder,
  })

  const updatePalette = useCallback(
    async (palette: Partial<Palette>) => {
      setGeneratingPalette(true)
      try {
        const newPalette = { ...currentTheme.palette, ...palette }
        const newTheme = await generateTheme(newPalette, { subfolder })
        updateCustomTheme(newTheme)
      } finally {
        setGeneratingPalette(false)
      }
    },
    [currentTheme, subfolder, updateCustomTheme],
  )

  const onSelectColorScheme = useCallback(
    (colorScheme: ColorScheme) => {
      setEditThemeMode(colorScheme)
      setPreviewDarkModeDisabled(true)
    },
    [setPreviewDarkModeDisabled],
  )

  if (showPresets) {
    return (
      <ColorPresets
        onSelect={theme => updateCustomTheme(theme)}
        onClose={() => setShowPresets(false)}
      />
    )
  }

  if (editThemeMode) {
    return (
      <AppearanceEditTheme
        colorScheme={editThemeMode}
        onClose={() => {
          setEditThemeMode(undefined)
          setPreviewDarkModeDisabled(false)
        }}
      />
    )
  }

  return (
    <SidebarContent
      header={<SidebarBackButton href="/manage/appearance" />}
      title={$t({ defaultMessage: 'Themes', id: 'Generics.Themes' })}
      description={$t({
        defaultMessage:
          'Choose the color scheme for your community that represents your brand.',
        id: 'Admin.Sidebar.ColorsDescription',
      })}
    >
      <ThemeCopyPaste />
      <div className="space-y-10">
        <div className="flex flex-col items-stretch">
          <div
            className={clsx([
              'relative group',
              'text-label-sm font-medium',
              'text-content',
              'flex items-center gap-3',
              'bg-surface',
              'px-4 py-4 rounded-base',
              'border border-line-subdued',
            ])}
          >
            <StyleSelector
              title={$t({
                defaultMessage: 'Theme color',
                id: 'Admin.Appearance.ThemeColor',
              })}
              icon={
                <div className="flex gap-1">
                  <ColorPicker
                    defaultColor={currentTheme.palette?.primary}
                    onChange={value => {
                      if (isGeneratingPalette) {
                        return
                      }
                      updatePalette({ primary: value })
                    }}
                    tooltip={$t({
                      defaultMessage: 'Primary colors',
                      id: 'Admin.Appearance.Palette.PrimaryColors',
                    })}
                    loading={isGeneratingPalette}
                    loadingLabel={$t({
                      defaultMessage: 'Generating theme...',
                      id: 'Admin.Appearance.Palette.GeneratingTheme',
                    })}
                  />
                </div>
              }
            />
          </div>

          <Link
            as="button"
            onClick={() => setShowPresets(true)}
            variant="accent"
            className="self-end mt-2 flex items-center gap-2 font-semibold text-sm"
          >
            <T
              id="Admin.Appearance.Palette.BrowseAllThemes"
              defaultMessage="Browse all themes"
            />
            <SvgIcon
              className="w-[1em] h-[1em] rtl:-scale-x-100"
              name="arrow-right"
            />
          </Link>
        </div>

        <ThemeMode onSelectColorScheme={onSelectColorScheme} />
      </div>
    </SidebarContent>
  )
}
